import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { adminAPI } from '../config/apiService';

// Settings -> Load Employees.
// Two sections: Employees (live) and Supervisors (stubbed for now).
//
// Employee flow: pick the Epicor "Employee Roster for Safety Incentives"
// xlsx -> parse client-side (SheetJS) -> preview counts -> Import. The
// backend proc SIP_Admin_Load_Employees upserts Departments, resets every
// IsActiveSIPEmployeeDrawing flag, updates matched employees, and inserts
// new ones with ManagersEmployeeID = 999.
//
// Expected columns (header row 1):
//   Employee Number | First Name | Last Name | Work Location | Job Title | Department | Status

const EXPECTED_LOCATIONS = ['V2', 'Sacramento', 'Las Vegas NV', 'Temple AZ', 'V1 - HQ', 'V3', 'Florida'];

function LoadEmployeesManager() {
    const fileRef = useRef(null);
    const [fileName, setFileName] = useState('');
    const [rows, setRows] = useState([]);          // parsed roster rows
    const [parseIssues, setParseIssues] = useState([]);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);    // { summary, skipped } from backend
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');

    // Supervisor import state (independent of the employee import above).
    const supRef = useRef(null);
    const [supFileName, setSupFileName] = useState('');
    const [supRows, setSupRows] = useState([]);
    const [supIssues, setSupIssues] = useState([]);
    const [supBusy, setSupBusy] = useState(false);
    const [supResult, setSupResult] = useState(null);
    const [supMsg, setSupMsg] = useState('');
    const [supMsgKind, setSupMsgKind] = useState('info');
    const supFlash = (text, kind = 'info') => { setSupMsg(text); setSupMsgKind(kind); };

    // Current rosters (shown on open; refreshed after each import).
    const [empRoster, setEmpRoster] = useState([]);
    const [supRoster, setSupRoster] = useState([]);
    const loadEmpRoster = async () => { try { const r = await adminAPI.getEmployeeRoster(); setEmpRoster(r.data || []); } catch { /* ignore */ } };
    const loadSupRoster = async () => { try { const r = await adminAPI.getSupervisorRoster(); setSupRoster(r.data || []); } catch { /* ignore */ } };
    useEffect(() => { loadEmpRoster(); loadSupRoster(); }, []);

    // Remove-employee popup (clicking an employee name in the roster).
    const [removeTarget, setRemoveTarget] = useState(null);  // { employeeID, name }
    const [removeBusy, setRemoveBusy] = useState(false);
    const confirmRemove = async () => {
        if (!removeTarget) return;
        setRemoveBusy(true);
        try {
            await adminAPI.setEmployeeInactive(removeTarget.employeeID);
            setRemoveTarget(null);
            await loadEmpRoster();
        } catch (e) {
            supFlash(`Remove failed: ${e?.response?.data || e?.message || 'unknown error'}`, 'error');
        } finally { setRemoveBusy(false); }
    };

    const flash = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setRows([]); setParseIssues([]); setResult(null); setMsg('');
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });

            const issues = [];
            const parsed = [];
            raw.forEach((r, i) => {
                const empId = parseInt(r['Employee Number'], 10);
                const status = String(r['Status'] || '').trim();
                if (!empId) { issues.push(`Row ${i + 2}: missing/invalid Employee Number`); return; }
                if (status && status.toLowerCase() !== 'active') { issues.push(`Row ${i + 2}: status "${status}" skipped`); return; }
                parsed.push({
                    EmployeeID: empId,
                    FirstName: String(r['First Name'] || '').trim(),
                    LastName: String(r['Last Name'] || '').trim(),
                    WorkLocation: String(r['Work Location'] || '').trim(),
                    JobTitle: String(r['Job Title'] || '').trim(),
                    Department: String(r['Department'] || '').trim()
                });
            });
            setRows(parsed);
            setParseIssues(issues);
            if (parsed.length === 0) flash('No usable rows found — check this is the Epicor roster export.', 'error');
        } catch {
            flash('Could not read that file. Is it a valid .xlsx?', 'error');
        }
    };

    const locationCounts = rows.reduce((acc, r) => {
        acc[r.WorkLocation] = (acc[r.WorkLocation] || 0) + 1;
        return acc;
    }, {});
    const unknownLocations = Object.keys(locationCounts).filter((l) => !EXPECTED_LOCATIONS.includes(l));

    const doImport = async () => {
        if (rows.length === 0) return;
        if (!window.confirm(
            `Import ${rows.length} employees?\n\n` +
            'This resets the SIP drawing flag for EVERYONE, then re-activates only the people in this file. ' +
            'Employees not in this file will no longer be in the drawing pool.'
        )) return;
        setBusy(true); setMsg(''); setResult(null);
        try {
            const res = await adminAPI.loadEmployees(rows);
            setResult(res.data);
            flash('Import complete.', 'success');
            loadEmpRoster();
        } catch (err) {
            flash(`Import failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally { setBusy(false); }
    };

    // ----- Supervisor import handlers -----
    const handleSupFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSupFileName(file.name);
        setSupRows([]); setSupIssues([]); setSupResult(null); setSupMsg('');
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
            const issues = [];
            const parsed = [];
            raw.forEach((r, i) => {
                const empId = parseInt(r['EmployeeID'], 10);
                const site = String(r['Site'] || '').trim();
                const role = String(r['Role'] || '').trim();
                if (!empId) { issues.push(`Row ${i + 2}: missing/invalid EmployeeID`); return; }
                parsed.push({ EmployeeID: empId, Site: site, Role: role });
            });
            setSupRows(parsed);
            setSupIssues(issues);
            if (parsed.length === 0) supFlash('No usable rows — check this is the supervisor import file.', 'error');
        } catch {
            supFlash('Could not read that file. Is it a valid .xlsx?', 'error');
        }
    };

    const doImportSupervisors = async () => {
        if (supRows.length === 0) return;
        if (!window.confirm(
            `Import ${supRows.length} supervisors?\n\n` +
            'This sets every supervisor inactive, then re-activates only those in this file (history is kept).'
        )) return;
        setSupBusy(true); setSupMsg(''); setSupResult(null);
        try {
            const res = await adminAPI.loadSupervisors(supRows);
            setSupResult(res.data);
            supFlash('Import complete.', 'success');
            loadSupRoster();
        } catch (err) {
            supFlash(`Import failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally { setSupBusy(false); }
    };

    return (
        <div>
            {/* ---------------- Employees ---------------- */}
            <div style={styles.card}>
                <div style={styles.eyebrow}>Load Employees</div>
                <div style={styles.title}>Employee roster</div>
                <p style={styles.muted}>
                    Upload the &ldquo;Employee Roster for Safety Incentives&rdquo; spreadsheet.
                    Departments are upserted, everyone&rsquo;s drawing flag is reset, and only
                    the people in this file are re-activated. New employees get placeholder
                    manager 999.
                </p>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFile}
                        style={{ fontSize: 13 }}
                    />
                    <button
                        onClick={doImport}
                        disabled={busy || rows.length === 0}
                        style={btn('#2563eb', busy || rows.length === 0)}
                    >
                        {busy ? 'Importing…' : `Import ${rows.length || ''} employees`.trim()}
                    </button>
                </div>

                {fileName && rows.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <div style={styles.previewTitle}>Preview — {rows.length} active rows from {fileName}</div>
                        <table style={styles.countTable}>
                            <thead>
                                <tr><th style={styles.th}>Work Location</th><th style={{ ...styles.th, textAlign: 'right' }}>Employees</th></tr>
                            </thead>
                            <tbody>
                                {Object.entries(locationCounts).sort((a, b) => a[0].localeCompare(b[0])).map(([loc, n]) => (
                                    <tr key={loc}>
                                        <td style={{ ...styles.td, color: EXPECTED_LOCATIONS.includes(loc) ? '#111827' : '#dc2626' }}>
                                            {loc}{!EXPECTED_LOCATIONS.includes(loc) && ' (unrecognized — will be skipped)'}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{n}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {unknownLocations.length > 0 && (
                            <div style={{ ...styles.muted, color: '#dc2626', marginTop: 6 }}>
                                Unrecognized locations get skipped server-side and reported after import.
                            </div>
                        )}
                    </div>
                )}

                {parseIssues.length > 0 && (
                    <details style={{ marginTop: 10 }}>
                        <summary style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
                            {parseIssues.length} row{parseIssues.length === 1 ? '' : 's'} skipped while reading the file
                        </summary>
                        <ul style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 0', paddingLeft: 18 }}>
                            {parseIssues.slice(0, 50).map((s, i) => <li key={i}>{s}</li>)}
                            {parseIssues.length > 50 && <li>… and {parseIssues.length - 50} more</li>}
                        </ul>
                    </details>
                )}

                {msg && <Banner kind={msgKind}>{msg}</Banner>}

                {result?.summary && (
                    <div style={{ marginTop: 12 }}>
                        <div style={styles.previewTitle}>Import results</div>
                        <table style={styles.countTable}>
                            <tbody>
                                <tr><td style={styles.td}>Rows processed</td><td style={{ ...styles.td, textAlign: 'right' }}>{result.summary.rowsProcessed}</td></tr>
                                <tr><td style={styles.td}>Employees updated</td><td style={{ ...styles.td, textAlign: 'right' }}>{result.summary.employeesUpdated}</td></tr>
                                <tr><td style={styles.td}>Employees added (manager 999)</td><td style={{ ...styles.td, textAlign: 'right' }}>{result.summary.employeesAdded}</td></tr>
                                <tr><td style={styles.td}>Departments updated</td><td style={{ ...styles.td, textAlign: 'right' }}>{result.summary.departmentsUpdated}</td></tr>
                                <tr><td style={styles.td}>Departments added</td><td style={{ ...styles.td, textAlign: 'right' }}>{result.summary.departmentsAdded}</td></tr>
                                <tr><td style={styles.td}>Rows skipped</td><td style={{ ...styles.td, textAlign: 'right', color: result.summary.rowsSkipped > 0 ? '#dc2626' : '#111827' }}>{result.summary.rowsSkipped}</td></tr>
                            </tbody>
                        </table>
                        {result.skipped?.length > 0 && (
                            <details style={{ marginTop: 8 }} open>
                                <summary style={{ fontSize: 12, color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>
                                    Skipped rows ({result.skipped.length})
                                </summary>
                                <table style={{ ...styles.countTable, marginTop: 6 }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Employee #</th><th style={styles.th}>Name</th>
                                            <th style={styles.th}>Location</th><th style={styles.th}>Department</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.skipped.map((s, i) => (
                                            <tr key={i}>
                                                <td style={styles.td}>{s.employeeID ?? '—'}</td>
                                                <td style={styles.td}>{s.firstName} {s.lastName}</td>
                                                <td style={styles.td}>{s.workLocation}</td>
                                                <td style={styles.td}>{s.departmentDescription}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </details>
                        )}
                    </div>
                )}

                <RosterList
                    title={`Current employee roster (${empRoster.length})`}
                    rows={empRoster}
                    cols={[['Site', 'site'], ['Name', null], ['Department', 'department']]}
                    onName={(r) => setRemoveTarget({ employeeID: r.employeeID, name: `${r.firstName} ${r.lastName}` })}
                />
            </div>

            {/* ---------------- Supervisors ---------------- */}
            <div style={{ ...styles.card, marginTop: 12 }}>
                <div style={styles.eyebrow}>Load Supervisors</div>
                <div style={styles.title}>Supervisor roster</div>
                <p style={styles.muted}>
                    Columns: <strong>FirstName · LastName · EmployeeID · Site · Role</strong>. The
                    employee must already exist (rows whose EmployeeID isn&rsquo;t in Employees
                    fail and are listed). Site is the code (VAL01 / VAL02 / SAC05). Anyone not in
                    this file is marked inactive (their history is kept).
                </p>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                    <input ref={supRef} type="file" accept=".xlsx,.xls" onChange={handleSupFile} style={{ fontSize: 13 }} />
                    <button onClick={doImportSupervisors} disabled={supBusy || supRows.length === 0} style={btn('#2563eb', supBusy || supRows.length === 0)}>
                        {supBusy ? 'Importing…' : `Import ${supRows.length || ''} supervisors`.trim()}
                    </button>
                </div>

                {supFileName && supRows.length > 0 && (
                    <div style={{ ...styles.previewTitle, marginTop: 12 }}>{supRows.length} rows from {supFileName}</div>
                )}

                {supIssues.length > 0 && (
                    <details style={{ marginTop: 8 }}>
                        <summary style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>{supIssues.length} row(s) skipped while reading the file</summary>
                        <ul style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 0', paddingLeft: 18 }}>
                            {supIssues.slice(0, 50).map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                    </details>
                )}

                {supMsg && <Banner kind={supMsgKind}>{supMsg}</Banner>}

                {supResult?.summary && (
                    <div style={{ marginTop: 12 }}>
                        <div style={styles.previewTitle}>Import results</div>
                        <table style={styles.countTable}>
                            <tbody>
                                <tr><td style={styles.td}>Rows processed</td><td style={{ ...styles.td, textAlign: 'right' }}>{supResult.summary.rowsProcessed}</td></tr>
                                <tr><td style={styles.td}>Supervisors updated</td><td style={{ ...styles.td, textAlign: 'right' }}>{supResult.summary.supervisorsUpdated}</td></tr>
                                <tr><td style={styles.td}>Supervisors added</td><td style={{ ...styles.td, textAlign: 'right' }}>{supResult.summary.supervisorsAdded}</td></tr>
                                <tr><td style={styles.td}>Rows skipped</td><td style={{ ...styles.td, textAlign: 'right', color: supResult.summary.rowsSkipped > 0 ? '#dc2626' : '#111827' }}>{supResult.summary.rowsSkipped}</td></tr>
                            </tbody>
                        </table>
                        {supResult.skipped?.length > 0 && (
                            <details style={{ marginTop: 8 }} open>
                                <summary style={{ fontSize: 12, color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>Skipped rows ({supResult.skipped.length})</summary>
                                <table style={{ ...styles.countTable, marginTop: 6 }}>
                                    <thead><tr><th style={styles.th}>Employee #</th><th style={styles.th}>Site</th><th style={styles.th}>Role</th><th style={styles.th}>Reason</th></tr></thead>
                                    <tbody>
                                        {supResult.skipped.map((s, i) => (
                                            <tr key={i}>
                                                <td style={styles.td}>{s.employeeID ?? '—'}</td>
                                                <td style={styles.td}>{s.siteCode}</td>
                                                <td style={styles.td}>{s.role}</td>
                                                <td style={{ ...styles.td, color: '#dc2626' }}>{s.reason}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </details>
                        )}
                    </div>
                )}

                <RosterList
                    title={`Current supervisor roster (${supRoster.length})`}
                    rows={supRoster}
                    cols={[['Site', 'site'], ['Name', null], ['Role', 'role']]}
                />
            </div>

            {removeTarget && (
                <div style={styles.modalOverlay} onClick={() => !removeBusy && setRemoveTarget(null)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalTitle}>Remove employee</div>
                        <p style={{ ...styles.muted }}>
                            Mark <strong>{removeTarget.name}</strong> (#{removeTarget.employeeID}) as
                            <strong> no longer an employee</strong>? This clears their drawing flag and
                            removes them from the roster. Their historic data is kept.
                        </p>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                            <button onClick={() => setRemoveTarget(null)} disabled={removeBusy} style={btn('#6b7280', removeBusy)}>Cancel</button>
                            <button onClick={confirmRemove} disabled={removeBusy} style={btn('#dc2626', removeBusy)}>{removeBusy ? 'Removing…' : 'Remove employee'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Collapsible current-roster list. cols = [[header, fieldKey|null], ...];
// null fieldKey renders "Last, First #id". When onName is given, the name
// becomes a link that fires onName(row) (used to open the remove popup).
function RosterList({ title, rows, cols, onName }) {
    if (!rows || rows.length === 0) return null;
    return (
        <details style={{ marginTop: 14 }}>
            <summary style={{ fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>{title}</summary>
            <table style={{ ...styles.countTable, marginTop: 8, width: '100%' }}>
                <thead>
                    <tr>{cols.map(([h]) => <th key={h} style={styles.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.employeeID}>
                            {cols.map(([h, key]) => (
                                <td key={h} style={styles.td}>
                                    {key ? r[key] : (
                                        onName ? (
                                            <button type="button" onClick={() => onName(r)} style={styles.nameLink} title="Remove employee…">
                                                {r.lastName}, {r.firstName} <span style={{ color: '#9ca3af', fontSize: 11 }}>#{r.employeeID}</span>
                                            </button>
                                        ) : (
                                            <>{r.lastName}, {r.firstName} <span style={{ color: '#9ca3af', fontSize: 11 }}>#{r.employeeID}</span></>
                                        )
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </details>
    );
}

function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 4,
    fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    muted: { color: '#6b7280', fontSize: 13, lineHeight: 1.5 },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    title: { fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2, marginBottom: 6 },
    previewTitle: { fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 },
    countTable: { borderCollapse: 'collapse', minWidth: 320 },
    th: { textAlign: 'left', padding: '4px 10px', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb' },
    td: { padding: '4px 10px', fontSize: 13, color: '#111827', borderBottom: '1px solid #f3f4f6' },
    nameLink: { background: 'none', border: 'none', padding: 0, color: '#2563eb', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
    modal: { background: 'white', borderRadius: 10, padding: 20, maxWidth: 420, width: '90%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
    modalTitle: { fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 8 },
};

export default LoadEmployeesManager;
